"use client";

import { useCallback, useMemo } from "react";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  SECONDARY_SOURCE_FIELDS as F,
  formatSecondarySource,
  type SecondarySourceLabels,
  secondarySourceIsReady,
} from "@/lib/tool-kit/special-sources/secondary-source-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const SECONDARY_SOURCE_CITATION_GENERATOR_STRINGS = {
  formHeading: "The two sources",
  resultsHeading: "Your citations",
  emptyPrompt:
    "Enter the original author and the author of the work you read to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  originalAuthorsLabel: "Original author(s) — the work you did not read",
  originalAuthorsPlaceholder: "Louis Zukofsky",
  originalAuthorsHint: "One per line, first name then surname.",
  originalYearLabel: "Year of the original (if known)",
  originalYearPlaceholder: "1931",
  originalYearHint:
    "Take it from the work you read. Leave it blank if that work does not give it — don't write n.d.",
  secondaryAuthorsLabel: "Author(s) of the work you read",
  secondaryAuthorsPlaceholder: "Bonnie Costello",
  secondaryYearLabel: "Year of the work you read",
  secondaryYearPlaceholder: "1981",
  pageLabel: "Page in the work you read",
  pagePlaceholder: "78",
  useLabel: "Are you quoting or paraphrasing the original?",
  useQuote: "Quoting its words",
  useParaphrase: "Paraphrasing",
  useHint:
    "Cite Them Right writes quoted in for a quotation and cited in for a paraphrase.",
  originalTitleLabel: "Title of the original (for Chicago)",
  originalTitlePlaceholder: "Sincerity and objectification",
  secondaryTitleLabel: "Title of the work you read (for Chicago)",
  secondaryTitlePlaceholder: "Marianne Moore: Imaginary possessions",
  kindLabelOriginal: "The original is",
  kindLabelSecondary: "The work you read is",
  kindArticle: "An article or chapter",
  kindBook: "A book",
  containerLabelOriginal: "Journal or book it appeared in (if an article)",
  containerLabelSecondary: "Journal or book it appeared in (if an article)",
  containerPlaceholder: "Poetry",
  detailsLabelOriginal: "Volume and date, or the publisher (for Chicago)",
  detailsPlaceholderOriginal: "37 (February 1931)",
  detailsLabelSecondary: "Volume and date, or the publisher (for Chicago)",
  detailsPlaceholderSecondary: "Harvard University Press",
  originalPageLabel:
    "Page of the original, as the work you read gives it (for Chicago)",
  originalPagePlaceholder: "269",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  harvardLabel: "Harvard (Cite Them Right)",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  mlaAuthorInProse: "Original author named in your sentence",
  mlaAuthorInParentheses: "Original author named in the citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliographyOriginal: "Bibliography — the original, with Quoted in",
  bibliographySecondary: "Bibliography — the work you read",
  apaNote:
    "Your reference list gets one entry: {secondary}, the work you read. The original has none.",
  mlaNote:
    "Your Works Cited list gets one entry: the work by {secondary} that you read.",
  chicagoNote:
    "Chicago lists both works. Its bibliography entry for the original ends with a Quoted in sentence.",
  chicagoNeedsTitles:
    "Chicago's note names both works in full: add both titles to see it.",
  harvardNote: "Your reference list gets one entry: the work you read.",
  example: {
    originalAuthors: "Louis Zukofsky",
    originalYear: "1931",
    secondaryAuthors: "Bonnie Costello",
    secondaryYear: "1981",
    page: "78",
    use: "quote",
    originalTitle: "Sincerity and objectification",
    originalKind: "article",
    originalContainer: "Poetry",
    originalDetails: "37 (February 1931)",
    originalPage: "269",
    secondaryTitle: "Marianne Moore: Imaginary possessions",
    secondaryKind: "book",
    secondaryDetails: "Harvard University Press",
  },
} as const;

type Strings = typeof SECONDARY_SOURCE_CITATION_GENERATOR_STRINGS;

function kindOptions(strings: Strings) {
  // "book" first: it is what an untouched select shows and what the formatter assumes.
  return [
    { value: "book", label: strings.kindBook },
    { value: "article", label: strings.kindArticle },
  ];
}

function buildFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.originalAuthors,
      kind: "people",
      label: strings.originalAuthorsLabel,
      placeholder: strings.originalAuthorsPlaceholder,
      hint: strings.originalAuthorsHint,
    },
    {
      id: F.secondaryAuthors,
      kind: "people",
      label: strings.secondaryAuthorsLabel,
      placeholder: strings.secondaryAuthorsPlaceholder,
    },
    {
      id: F.originalYear,
      kind: "text",
      label: strings.originalYearLabel,
      placeholder: strings.originalYearPlaceholder,
      hint: strings.originalYearHint,
    },
    {
      id: F.secondaryYear,
      kind: "text",
      label: strings.secondaryYearLabel,
      placeholder: strings.secondaryYearPlaceholder,
    },
    {
      id: F.page,
      kind: "text",
      label: strings.pageLabel,
      placeholder: strings.pagePlaceholder,
    },
    {
      id: F.use,
      kind: "select",
      label: strings.useLabel,
      hint: strings.useHint,
      options: [
        { value: "quote", label: strings.useQuote },
        { value: "paraphrase", label: strings.useParaphrase },
      ],
    },
    {
      id: F.originalTitle,
      kind: "text",
      label: strings.originalTitleLabel,
      placeholder: strings.originalTitlePlaceholder,
    },
    {
      id: F.secondaryTitle,
      kind: "text",
      label: strings.secondaryTitleLabel,
      placeholder: strings.secondaryTitlePlaceholder,
    },
    {
      id: F.originalKind,
      kind: "select",
      label: strings.kindLabelOriginal,
      options: kindOptions(strings),
    },
    {
      id: F.secondaryKind,
      kind: "select",
      label: strings.kindLabelSecondary,
      options: kindOptions(strings),
    },
    {
      id: F.originalContainer,
      kind: "text",
      label: strings.containerLabelOriginal,
      placeholder: strings.containerPlaceholder,
    },
    {
      id: F.secondaryContainer,
      kind: "text",
      label: strings.containerLabelSecondary,
    },
    {
      id: F.originalDetails,
      kind: "text",
      label: strings.detailsLabelOriginal,
      placeholder: strings.detailsPlaceholderOriginal,
    },
    {
      id: F.secondaryDetails,
      kind: "text",
      label: strings.detailsLabelSecondary,
      placeholder: strings.detailsPlaceholderSecondary,
    },
    {
      id: F.originalPage,
      kind: "text",
      label: strings.originalPageLabel,
      placeholder: strings.originalPagePlaceholder,
      wide: true,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function secondarySourceLabels(strings: Strings): SecondarySourceLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    harvard: strings.harvardLabel,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    mlaAuthorInProse: strings.mlaAuthorInProse,
    mlaAuthorInParentheses: strings.mlaAuthorInParentheses,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliographyOriginal: strings.bibliographyOriginal,
    bibliographySecondary: strings.bibliographySecondary,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
    chicagoNeedsTitles: strings.chicagoNeedsTitles,
    harvardNote: strings.harvardNote,
  };
}

export default function SecondarySourceCitationGeneratorWidget() {
  const strings = useToolUi(SECONDARY_SOURCE_CITATION_GENERATOR_STRINGS);
  const fields = useMemo(() => buildFields(strings), [strings]);
  const labels = useMemo(() => secondarySourceLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatSecondarySource(values, labels),
    [labels]
  );

  return (
    <SourceCitationBuilder
      example={strings.example}
      fields={fields}
      format={format}
      isReady={secondarySourceIsReady}
      strings={strings}
    />
  );
}
